// Company-domain helpers — single source of truth for turning user-entered
// domain text into a bare host, and for deriving a fallback company/workspace
// name from it. Shared so the create-workspace modal, the onboarding wizard,
// and settings all normalize identically (they used to diverge — one stripped
// `www.`, the other didn't).

/** Strip scheme, `www.`, and any path/query, returning the bare host — e.g.
 * `https://www.acme.com/pricing` → `acme.com`. */
export function normalizeDomain(raw: string): string {
  return raw
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/.*$/, '')
    .trim();
}

/** True only for a registrable-looking DNS host, never a path, port, or label. */
export function isValidDomain(raw: string): boolean {
  const host = normalizeDomain(raw).toLowerCase();
  if (!host || host.length > 253 || host.includes(':') || host.includes('..')) return false;
  const labels = host.split('.');
  if (labels.length < 2) return false;
  const labelPattern = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
  return labels.every((label) => labelPattern.test(label)) && labels.at(-1)!.length >= 2;
}

/** Derive a title-cased name from a domain's first label — e.g. `acme.com` →
 * `Acme`. Expects an already-normalized host but tolerates raw input. */
export function nameFromDomain(domain: string): string {
  const host = normalizeDomain(domain);
  const label = host.split('.')[0] || host;
  return label.charAt(0).toUpperCase() + label.slice(1);
}
